/**
 * Coordinates saved on a customer's address record when Onfleet could not geocode
 * it (confirmed in the Shipments page's Find on map dialog, saved by
 * fed-nutrition-api/src/services/v2/geocode.ts).  They keep a copy of the address
 * they were set for (`addressUsed`), so an edited address stops using them;
 * coordinates saved before the copy was kept are matched by `addressKey`.  The
 * matching must agree with the api's copy (and fed-lib src/address-coordinates.js).
 */
export function normalizePostalCode(code) {
  return String(code || '').replace(/\s+/g, '').toUpperCase();
}

export function addressKey(address) {
  if (!address || typeof address !== 'object') return '';
  const line1 = String(address.line1 || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const city = String(address.city || '').trim().toLowerCase();
  return [line1, city, normalizePostalCode(address.postal_code)].join('|');
}

/** the address fields a pin is set for: a change to any of them retires the coordinates (a unit is in the same building) */
const ADDRESS_FIELDS = ['line1', 'line2', 'city', 'state', 'postal_code', 'country'];

function normalizedField(field, value) {
  const text = String(value == null ? '' : value).trim().replace(/\s+/g, ' ').toLowerCase();
  return field === 'postal_code' ? text.replace(/\s+/g, '') : text;
}

/** the same address, ignoring case and spacing */
export function sameAddress(a, b) {
  return ADDRESS_FIELDS.every(field => normalizedField(field, a && a[field]) === normalizedField(field, b && b[field]));
}

/** 'current' when the saved coordinates are for the address as it is now, 'stale' when the address changed since, null when none */
export function coordinatesState(address) {
  const coordinates = address && address.coordinates;
  if (!coordinates || !isFinite(Number(coordinates.lat)) || !isFinite(Number(coordinates.lng))) return null;
  const matches = coordinates.addressUsed
    ? sameAddress(coordinates.addressUsed, address)
    : coordinates.forAddress === addressKey(address);
  return matches ? 'current' : 'stale';
}

/**
 * a "lat, lng" pair as typed (or copied from Google Maps), or {lat, lng}; throws when it is
 * not two numbers in British Columbia.  The api checks the same (geocode.ts parseLatLng).
 */
export function parseLatLng(input) {
  let lat;
  let lng;
  if (input && typeof input === 'object') {
    lat = Number(input.lat);
    lng = Number(input.lng);
  } else {
    const parts = String(input || '').trim().split(/[\s,]+/).filter(Boolean);
    if (parts.length !== 2) throw new Error('enter the coordinates as "latitude, longitude", eg 49.2827, -123.1207');
    [lat, lng] = parts.map(Number);
  }
  if (!isFinite(lat) || !isFinite(lng)) {
    throw new Error('the coordinates must be numbers, eg 49.2827, -123.1207');
  }
  if (lat < 48 || lat > 60 || lng < -140 || lng > -114) {
    throw new Error(`${lat}, ${lng} is not in British Columbia - check the order (latitude first) and the minus sign`);
  }
  return {lat, lng};
}

export function mapsUrl({lat, lng}) {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

/**
 * the address the export sends with a stop's saved coordinates.  the one-line address
 * is what onfleet geocodes, but onfleet refuses it next to a location: a task sent with
 * coordinates needs a parsed address, with city and country at least.
 */
export function onfleetParsedAddress(address) {
  return {
    line1: String(address.line1 || '').trim(),
    line2: String(address.line2 || '').trim(),
    // "Squamish (HOURS REMOVE THIS)" is Squamish, as formatAddress shows it
    city: String(address.city || '').replace(/\(.+\)/g, '').trim(),
    state: address.state || 'BC',
    postal_code: normalizePostalCode(address.postal_code),
    country: address.country || 'CA',
    location: address.location
  };
}
